import { useCallback, useState } from 'react'
import PaginationC from '@/gf/components/Pagination'
import SearchInput from '@/gf/components/SearchInput'
import {
  PhaseCode,
  useDeletePhaseCodeMutation,
  usePaginatedPhaseCodesQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import usePage from '@/gf/hooks/usePage'
import { Table, Td, Th, Tr } from '@/gf/components/next/Table'
import debounce from 'lodash/debounce'
import Action from '@/gf/components/Action'
import Ghost from '@/gf/components/Ghost'
import useModal from '@/gf/hooks/useModal'
import { Maybe } from '@/types'
import Box from '@/gf/components/Box'
import Page from '@/gf/components/Page'
import Frame from '@/buyers/components/Frame'
import EditPhaseCodeModal from './EditPhaseCodeModal'
import ConfirmationModal from '@/gf/components/ConfirmationModal'
import useMsgs from '@/gf/hooks/useMsgs'

const Loading = () => (
  <>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((a) => (
      <Tr key={a}>
        <Td className="w-20">
          <Ghost className="h-4 w-1/2 block" />
        </Td>
        <Td>
          <Ghost className="h-4 w-3/4 block" />
        </Td>
        <Td>
          <Ghost className="h-4 w-10 block" />
        </Td>
      </Tr>
    ))}
  </>
)
const PhaseCodesTable = () => {
  const client = useGqlClient()
  const [_, msgs] = useMsgs()
  const [page, setPage] = usePage()
  const [searchTerm, setSearchTerm] = useState('')
  const phaseCodeModal = useModal<{ phaseCode: Maybe<PhaseCode> }>()
  const deleteConfirmationModal = useModal<{ id: string }>()

  const [deleteMutation] = useDeletePhaseCodeMutation({ client })
  const { data, previousData } = usePaginatedPhaseCodesQuery({
    client,
    variables: {
      page,
      searchTerm,
    },
  })

  const onSearchChanged = useCallback(
    debounce((text: string) => {
      setSearchTerm(text)
      setPage(1)
    }, 300),
    []
  )

  const deletePhaseCode = async (id: string) => {
    try {
      await deleteMutation({ variables: { id } })

      client.reFetchObservableQueries()

      msgs.add('Phase code deleted!', 'positive')
    } catch (err) {
      msgs.add('Phase Code cannot be deleted once it is linked to an order.', 'negative')
    } finally {
      deleteConfirmationModal.close()
    }
  }

  return (
    <Frame
      breadcrumbs={{
        copy: '',
        crumbs: [
          { name: 'Settings', href: '/settings' },
          { name: 'Organization', href: '/settings/organization' },
          { name: 'Phase Codes', href: '/settings/organization/phase-codes' },
        ],
      }}
    >
      <Page
        title="Phase Codes"
        actionsNext={[
          <Action.P onClick={() => phaseCodeModal.open({ phaseCode: null })}>
            Add Phase Code
          </Action.P>,
        ]}
        previousPage={'/settings/organization'}
      >
        <ConfirmationModal
          open={deleteConfirmationModal.isOpen}
          onCancel={deleteConfirmationModal.close}
          onConfirm={() => {
            if (deleteConfirmationModal.props) {
              deletePhaseCode(deleteConfirmationModal.props.id)
            }
          }}
          title="Delete Phase Code"
          text="Are you sure you want to delete this phase code? This action cannot be undone."
        />
        <Box className="p-6 mt-6">
          {phaseCodeModal.isOpen && (
            <EditPhaseCodeModal
              open
              onClose={phaseCodeModal.close}
              phaseCode={phaseCodeModal.props?.phaseCode ?? null}
            />
          )}

          <div className="space-y-4">
            <div>
              <SearchInput
                value={searchTerm}
                onChange={onSearchChanged}
                placeholder="Search by phase code or name"
                autoFocus
                autoCompleteOff
              />
            </div>

            <Table className="text-sm">
              <thead>
                <Tr>
                  <Th className="w-40">Code</Th>
                  <Th>Name</Th>
                  <Th className="w-24" />
                </Tr>
              </thead>
              <tbody>
                {!data ? (
                  <Loading />
                ) : (
                  <>
                    {data?.phaseCodes.entries.map((j) => (
                      <Tr key={j.id}>
                        <Td>{j.code}</Td>
                        <Td>{j.name} </Td>

                        <Td>
                          <div className="flex gap-x-4">
                            <Action.T
                              onClick={() => phaseCodeModal.open({ phaseCode: j })}
                              className="text-sm font-medium no-underline cursor-pointer"
                            >
                              Edit
                            </Action.T>

                            <Action.T
                              onClick={() => deleteConfirmationModal.open({ id: j.id })}
                              className="text-sm font-medium no-underline cursor-pointer"
                            >
                              Delete
                            </Action.T>
                          </div>
                        </Td>
                      </Tr>
                    ))}
                    {data.phaseCodes.entries.length === 0 && searchTerm && (
                      <Tr>
                        <Td className="text-gray-700" colSpan={4}>
                          No results for{' '}
                          <span className="text-gray-900 font-medium">{searchTerm}</span>
                        </Td>
                      </Tr>
                    )}
                  </>
                )}
              </tbody>
            </Table>

            <PaginationC
              page={page}
              pagination={(data ?? previousData)?.phaseCodes.pagination}
              updatePage={setPage}
            />
          </div>
        </Box>
      </Page>
    </Frame>
  )
}

export default PhaseCodesTable
